import React from 'react';
import styles from 'components/layout_and_quote_builder/_style.module.scss';
import MachineSheetRectangle from 'components/layout_and_quote_builder/MachineSheetRectangle.jsx';

// One card per ManufacturingLine (a machine configuration): for an offset_pair, a printing machine + a
// die-cutting machine; for a flexo_combined, a single combined printing_and_die_cutting_machine. Styled
// like the solution cards. Solutions for the manufacturingLine are searched once it is selected — onSelect.
const ManufacturingLineCard = ({ manufacturingLine, onSelect, maxMachineDimensionMm }) => {
  // Machine sheets are drawn to scale (against the largest machine across all listed ManufacturingLines)
  // by the shared MachineSheetRectangle; this card adds the familiar width X height shorthand below it.
  const toDisplay = (valueMm) => (
    window.user_unit == 'in'
      ? `${(parseFloat(valueMm) / 25.4).toFixed(2)}in`
      : `${Math.round(parseFloat(valueMm))}mm`
  );

  // Each machine is its own inner card inside the ManufacturingLine card, so the printing and die-cutting
  // machines read as two distinct things. The PRINTING/CUTTING headers are a neutral black, same for both.
  const machineColumn = (label, machineName, widthMm, heightMm, kind) => (
    <div className={styles.layout_column} style={{
      display: 'flex', flexDirection: 'column',
      padding: '14px 10px', borderRadius: '10px', background: '#fff',
      border: '1px solid #ececec', boxShadow: '0 1px 4px rgba(0, 0, 0, 0.05)'
    }}>
      <div className={styles.layout_column_label} style={{ color: '#222' }}>{label}</div>
      {/* Machine name is the hero of the card — the largest, darkest text. */}
      <div style={{ fontSize: '13px', fontWeight: 600, color: '#222', lineHeight: 1.3, marginTop: '2px' }}>{machineName}</div>

      <div style={{ marginTop: 'auto' }}>
        <MachineSheetRectangle widthMm={widthMm} heightMm={heightMm} kind={kind} maxMm={maxMachineDimensionMm} />
        {/* Redundant with the bed's own edge labels, so it's the quietest line — small and gray. */}
        {widthMm && heightMm ? (
          <div style={{ fontSize: '12px', fontWeight: 400, color: '#888', marginTop: '2px' }}>{toDisplay(widthMm)} X {toDisplay(heightMm)}</div>
        ) : null}
      </div>
    </div>
  );

  const machineColumns = () => {
    if (manufacturingLine.type === 'flexo_combined') {
      return machineColumn(
        `${t('layouts.layout_type_die_cutting')} + ${t('layouts.layout_type_printing')}`,
        manufacturingLine.printing_and_die_cutting_machine_label,
        manufacturingLine.printing_sheet_width_mm,
        manufacturingLine.printing_sheet_height_mm,
        'combined'
      );
    }

    // Printing first, then die-cutting — the real production order, matching the solution cards.
    return (
      <>
        {machineColumn(t('layouts.layout_type_printing'), manufacturingLine.printing_machine_label, manufacturingLine.printing_sheet_width_mm, manufacturingLine.printing_sheet_height_mm, 'printing')}
        {machineColumn(t('layouts.layout_type_die_cutting'), manufacturingLine.die_cutting_machine_label, manufacturingLine.sheet_width_mm, manufacturingLine.sheet_height_mm, 'die_cutting')}
      </>
    );
  };

  // Which of the section's chosen material sheets (e.g. B3/B2/B1) this ManufacturingLine can run. A
  // line is listed when it runs at least one of the chosen sheets, so without these chips two listed
  // lines would look interchangeable even when one runs every sheet and the other only the smallest.
  const materialDimensionChips = () => {
    const materialLayoutDimensions = manufacturingLine.material_layout_dimensions || [];
    if (materialLayoutDimensions.length === 0) {
      return null;
    }

    return (
      <div style={{ textAlign: 'center', marginTop: '10px' }}>
        <div style={{ fontSize: '12px', color: '#888', marginBottom: '4px' }}>
          {t('quote_builder.material_dimensions')}:
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: '6px' }}>
          {materialLayoutDimensions.map((materialLayoutDimension) => (
            <span
              key={materialLayoutDimension.id}
              style={{
                fontSize: '12px', color: '#555', border: '1px solid #ccc', borderRadius: '4px',
                padding: '2px 8px', backgroundColor: '#f9f9f9'
              }}
            >
              {window.user_unit == 'in' ? materialLayoutDimension.description_in : materialLayoutDimension.description_mm}
            </span>
          ))}
        </div>
      </div>
    );
  };

  const handleClick = () => {
    if (onSelect) {
      onSelect(manufacturingLine);
    }
  };

  return (
    <div className={styles.combinations_grid_item_cont} data-manufacturing-line-identifier={manufacturingLine.identifier} onClick={handleClick}>
      <div className={styles.combinations_grid_item}>
        <div className={styles.layout_columns_container}>
          {machineColumns()}
        </div>
        {materialDimensionChips()}
        {/* How many of this line's solutions have a unit price (0 unless both machines are priced). */}
        <div style={{ textAlign: 'center', marginTop: '10px', fontSize: '13px' }}>
          {t('quote_builder.priced_solutions')}: <strong>{manufacturingLine.priced_solution_count ?? 0}</strong>
        </div>
      </div>
    </div>
  );
};

export default ManufacturingLineCard;
